import { motion } from "framer-motion";
import { MapPin, Navigation, Clock } from "lucide-react";
import { BRAND } from "../lib/brand";

const LANDMARKS = [
  "PLA Hisar",
  "Delhi Road Highway",
  "Hisar Airport",
  "Industrial Area",
];

const MAP_QUERY = "GF, 1115, Sector 33, Hisar, Haryana 125001";
const MAP_SRC =
  "https://maps.google.com/maps?q=" +
  encodeURIComponent(MAP_QUERY) +
  "&z=16&output=embed";
const DIRECTIONS =
  "https://www.google.com/maps/dir/?api=1&destination=" +
  encodeURIComponent(MAP_QUERY);

export const Location = () => {
  return (
    <section id="location" className="relative py-16 sm:py-24 lg:py-32" data-testid="location-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14 items-stretch">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#c5a059]" />
              <span className="text-xs tracking-[0.34em] uppercase text-[#9a7b38] font-semibold">
                Visit Our Office
              </span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#131311] leading-[1]">
              Find us in the heart of <span className="italic text-gold-gradient">Hisar.</span>
            </h2>

            <div className="mt-8 rounded-2xl border border-[#e7e3d7] bg-[#ffffff] p-6">
              <div className="flex gap-4">
                <div className="h-11 w-11 shrink-0 rounded-full bg-[#131311] grid place-items-center text-[#f0d98a]">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#8a8578]">
                    Office Address
                  </div>
                  <div className="font-serif text-xl text-[#131311] mt-1 leading-snug">
                    {BRAND.address}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-6 pt-6 border-t border-[#e7e3d7]">
                <div className="h-11 w-11 shrink-0 rounded-full bg-[#f4efdf] grid place-items-center text-[#9a7b38]">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#8a8578]">
                    Visiting Hours
                  </div>
                  <div className="text-[#131311] mt-1 text-sm leading-relaxed">
                    {BRAND.hours}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#8a8578] mb-3">
                Nearby & Connectivity
              </div>
              <div className="flex flex-wrap gap-2">
                {LANDMARKS.map((l) => (
                  <span
                    key={l}
                    className="rounded-full border border-[#e0dccf] px-4 py-2 text-sm text-[#52514c]"
                  >
                    {l}
                  </span>
                ))}
              </div>
            </div>

            <a
              href={DIRECTIONS}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-[#131311] text-[#faf9f5] px-7 py-4 text-sm font-semibold hover:bg-[#9a7b38] transition-colors duration-300"
              data-testid="map-directions-button"
            >
              <Navigation className="h-4 w-4" />
              Get Directions
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[2rem] p-2 bg-gradient-to-br from-[#f0d98a] via-[#c5a059] to-[#9a7b38] shadow-[0_40px_80px_-40px_rgba(19,19,17,0.4)] min-h-[420px]"
          >
            <iframe
              title="Laxmi Jiwan Properties location — Sector 33, Hisar"
              src={MAP_SRC}
              className="absolute inset-2 h-[calc(100%-1rem)] w-[calc(100%-1rem)] rounded-[1.6rem] grayscale-[15%]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="google-map-iframe"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
